import React, { useCallback, useEffect, useMemo } from "react";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
  Toast,
} from "components/commons";
import lang from "translations";
import { useApi, useFilter, useMount } from "hooks";
import { dashboardFilterState } from "./filters";
import { searchLocationWithTransaction } from "apis";
import { measureTime } from "services/date.service";
import { prettifyIntervalTime } from "services/pretty.service";

const DashboardFilter = ({ venue, venueId, fetchCharts }) => {
  const { modifyFilters, modifyFilter, clearFilter, filterState } = useFilter(
    dashboardFilterState(venue),
  );

  const {
    request: searchLocationRequest,
    loading,
    mappedData: locationOptions = [],
    error,
  } = useApi({
    api: searchLocationWithTransaction,
    params: {
      venueId,
    },
    mapper: {
      value: {
        key: "locationId",
      },
      text: {
        key: "locationName",
      },
    },
    isArray: true,
    handleOwnError: true,
  });

  const checkWarningDateRange = useCallback(([startDate, endDate]) => {
    const difference = Math.abs(startDate.diff(endDate, "day"));
    return difference > 365;
  }, []);

  const intervalOptions = useMemo(() => {
    const [startDate, endDate] = filterState.dateRange;
    return measureTime([startDate, endDate])
      .getOptType()
      .map((interval) => ({
        value: interval,
        text: prettifyIntervalTime(interval),
      }));
  }, [filterState.dateRange]);

  useEffect(() => {
    modifyFilter("interval", { ...intervalOptions[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalOptions]);

  const submit = useCallback(
    async (filter) => {
      const selectedLocations = filter.locationIds || [];
      await fetchCharts({
        ...filter,
        locationOptions: selectedLocations,
        locationIds: selectedLocations.map((location) => location.value),
      });
    },
    [fetchCharts],
  );

  const applyFilterCb = useCallback(async () => {
    const { requestState } = modifyFilters({ page: 1 });
    if (checkWarningDateRange(filterState.dateRange)) {
      Toast({
        content: lang.onlyDateRangesLessThanOneYear,
        error: true,
        icon: "exclamation-fill",
      }).open();
      return;
    }
    await submit(requestState);
  }, [checkWarningDateRange, filterState.dateRange, modifyFilters, submit]);

  const clearFilterCb = useCallback(async () => {
    const { requestState } = clearFilter();
    await submit(requestState);
  }, [clearFilter, submit]);

  const changeDateRange = useCallback(
    (dateRange) => {
      if (checkWarningDateRange(dateRange)) {
        Toast({
          content: lang.onlyDateRangesLessThanOneYear,
          error: true,
          icon: "exclamation-fill",
        }).open();
        return;
      }
      modifyFilters({
        dateRange,
        page: 1,
      });
    },
    [checkWarningDateRange, modifyFilters],
  );

  useMount(() => {
    searchLocationRequest({
      venueId,
      page: 1,
      pageSize: 100,
    });
    submit(filterState);
  });

  useEffect(() => {
    if (checkWarningDateRange(filterState.dateRange)) {
      Toast({
        content: lang.onlyDateRangesLessThanOneYear,
        error: true,
        icon: "exclamation-fill",
      }).open();
    }
  }, [checkWarningDateRange, filterState.dateRange]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      onApply={applyFilterCb}
      onClear={clearFilterCb}
      searchBox={false}
      syncStateOnApply={false}
      trackActions={false}
    >
      <FilterDateRange
        id="dashboardDateRange"
        label={lang.dates}
        maxDays={365}
        value={filterState.dateRange}
        onChange={changeDateRange}
      />
      <FilterTimeRange
        id="dashboardTimeRange"
        label={lang.time}
        value={filterState.dateRange}
        onChange={(dateRange) => modifyFilters({ dateRange, page: 1 })}
      />
      <FilterSingleSelect
        id="dashboardInterval"
        label={lang.interval}
        options={intervalOptions}
        summaryFallback={lang.interval}
        value={filterState.interval}
        onChange={(interval) => modifyFilter("interval", { value: interval })}
      />
      <FilterMultiSelect
        id="dashboardLocations"
        label={lang.location}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        disabled={error}
        loading={loading}
        options={locationOptions}
        value={filterState.locationIds}
        onChange={(locationIds) => modifyFilters({ locationIds: locationIds || [] })}
      />
    </FilterBar>
  );
};

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export default DashboardFilter;
